'use client';
import { CaptchaRequestMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import React, { useEffect } from 'react';

import { newsletterSubscribe } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Button } from '../ui/button';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
} from '@eliadmin/core/src/components/ui/alert-dialog';

import { env } from '@eliadmin/core/src/env';
import { LuX } from 'react-icons/lu';
import { useMutation } from '@tanstack/react-query';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

const COUPON_DIALOG_COUNTDOWN_STARTED_AT_KEY = 'coupon_dialog_countdown_started_at';
const COUPON_DIALOG_HIDDEN_UNTIL_KEY = 'coupon_dialog_hidden_until';
const COUPON_DIALOG_DURATION_MS = 24 * 60 * 60 * 1000;
const COUPON_DIALOG_HIDE_MS = 7 * 24 * 60 * 60 * 1000;

function getStoredTime(key: string) {
  const value = window.localStorage.getItem(key);
  const timestamp = value ? Number(value) : 0;

  return Number.isFinite(timestamp) ? timestamp : 0;
}

function formatRemainingTime(milliseconds: number) {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds].map(value => String(value).padStart(2, '0')).join(':');
}

export function CouponDialog({
  title,
  description,
  image,
}: {
  title: string;
  description: string;
  image: string;
}) {
  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(false);
  const [isSubscribed, setIsSubscribed] = React.useState(false);
  const [remainingMs, setRemainingMs] = React.useState(COUPON_DIALOG_DURATION_MS);

  const locale = useLocaleStore(s => s.currentLocale);

  const subscribeMutation = useMutation({
    mutationFn: newsletterSubscribe,
    onSuccess: () => setIsSubscribed(true),
  });

  const { isOpen, close, open } = useDisclosure();
  const { t } = useTranslations();

  const snoozeDialog = React.useCallback(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(
        COUPON_DIALOG_HIDDEN_UNTIL_KEY,
        String(Date.now() + COUPON_DIALOG_HIDE_MS),
      );
    }

    close();
  }, [close]);

  useEffect(() => {
    const now = Date.now();
    const hiddenUntil = getStoredTime(COUPON_DIALOG_HIDDEN_UNTIL_KEY);

    if (hiddenUntil > now) return;
    if (hiddenUntil) window.localStorage.removeItem(COUPON_DIALOG_HIDDEN_UNTIL_KEY);

    let countdownStartedAt = getStoredTime(COUPON_DIALOG_COUNTDOWN_STARTED_AT_KEY);
    const isInvalidCountdown =
      !countdownStartedAt ||
      countdownStartedAt > now ||
      now - countdownStartedAt >= COUPON_DIALOG_DURATION_MS;

    if (isInvalidCountdown) {
      countdownStartedAt = now;
      window.localStorage.setItem(
        COUPON_DIALOG_COUNTDOWN_STARTED_AT_KEY,
        String(countdownStartedAt),
      );
    }

    setRemainingMs(Math.max(0, COUPON_DIALOG_DURATION_MS - (now - countdownStartedAt)));
    open();
  }, [open]);

  useEffect(() => {
    if (!isOpen) return;

    const updateRemainingTime = () => {
      const countdownStartedAt = getStoredTime(COUPON_DIALOG_COUNTDOWN_STARTED_AT_KEY);
      const nextRemainingMs = Math.max(
        0,
        COUPON_DIALOG_DURATION_MS - (Date.now() - countdownStartedAt),
      );

      setRemainingMs(nextRemainingMs);
    };

    updateRemainingTime();
    const interval = window.setInterval(updateRemainingTime, 1000);

    return () => window.clearInterval(interval);
  }, [isOpen]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      open();
      return;
    }

    snoozeDialog();
  };

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!checked || isSubscribed || !email || subscribeMutation.isPending) return;
    snoozeDialog();
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: checked,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  return (
    <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
      <AlertDialogContent
        className={cn(
          'flex w-full flex-col items-center justify-start overflow-hidden rounded-[8px] border-none bg-white p-0 sm:max-w-[715px] md:flex-row',
          'ea-coupon-dialog__content',
        )}
      >
        <AlertDialogCancel className="absolute right-6 top-6 z-[10] h-fit w-fit border-none p-0 shadow-none hover:bg-transparent">
          <LuX size={24} className="text-white/70 md:text-black/30" />
        </AlertDialogCancel>
        <Image
          src={image}
          alt={title}
          width={0}
          height={0}
          className="h-[150px] w-full object-cover object-center sm:h-[220px] md:h-[375px] md:w-[225px]"
          sizes="(min-width: 768px) 225px, 100vw"
        />
        <form
          onSubmit={handleSubmit}
          className="flex w-full flex-col items-start justify-start gap-6 p-6 sm:gap-8 lg:p-8"
        >
<CaptchaRequestMessage error={subscribeMutation.error} />
          <div className="flex w-full flex-col items-start justify-start gap-4">
            <h3 className="text-balance text-2xl font-normal text-foreground sm:text-3xl">
              {title}
            </h3>
            <HtmlRendererRaw
              as="p"
              className="text-sm font-normal text-[#838383]"
              html={description}
            />
            <p className="rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary">
              Zľavový kód platí ešte {formatRemainingTime(remainingMs)}
            </p>
          </div>
          <div className="flex w-full flex-col items-start justify-start gap-2">
            <Input
              id="newsletter-email"
              type="email"
              name="email"
              placeholder="E-mailová adresa"
              onChange={e => setEmail(e.target.value)}
              className={{
                wrapper: cn(
                  'bg-whites rounded-full border border-border',
                  'ea-homepage-newsletter__form-item-email-input',
                ),
              }}
            />
            <div
              className={cn(
                'flex flex-row items-start space-x-2',
                'ea-homepage-newsletter__form-item-gdpr',
              )}
            >
              <Checkbox
                checked={checked}
                onCheckedChange={value => setChecked(value === true)}
                id="newsletter-gdpr"
                aria-label={t('text264') + ' ' + t('text265') + ' ' + t('text436')}
                className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox')}
                required
              />
              <Label
                htmlFor="newsletter-gdpr"
                className={cn(
                  'text-sm font-normal text-[#060B17]',
                  'ea-homepage-newsletter__form-item-gdpr-checkbox-label',
                )}
              >
                {t('text3587')}{' '}
                <Link prefetch={false} href={ROUTES.GDPR()} className="underline">
                  {t('text2342')}
                </Link>
              </Label>
            </div>
          </div>

          <div className="flex w-full items-center justify-end">
            <Button
              disabled={!checked || isSubscribed || !email || subscribeMutation.isPending}
              type="submit"
            >
              Získať zľavový kód
            </Button>
          </div>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
